import React, { Fragment } from 'react';
import PropTypes from 'prop-types';
import { connect } from 'react-redux';
import { bindActionCreators } from 'redux';

import { UsaStates } from 'usa-states';

import { withStyles } from '@material-ui/core/styles';
import withWidth from '@material-ui/core/withWidth';
import Grid from '@material-ui/core/Grid';
import InputAdornment from '@material-ui/core/InputAdornment';
import Typography from '@material-ui/core/Typography';

import { Field, Fields, reduxForm, SubmissionError } from 'redux-form';

import TextField, { TextFieldAdornmentText } from 'components/fields/TextField';
import AutosuggestField from 'components/fields/AutosuggestField';
import MultiselectAutosuggestField from 'components/fields/MultiselectAutosuggestField';
import FormError from 'components/common/FormError';
import IconLink from 'components/icons/IconLink';

import {
  createPrescriber,
  updatePrescriber,
  addPrescriberResource,
  removePrescriberResource,
} from 'redux/modules/prescribers';
import { requestFetchPlacesAutocomplete } from 'redux/modules/placesAutocomplete';
import {
  requestFetchReferralSources,
  requestSearchReferralSources,
} from 'redux/modules/referralSources';
import { accountLoginFail } from 'redux/modules/account';

import * as validate from 'utils/validate';
import * as normalize from 'utils/normalize';
import objectDiff from 'utils/objectDiff';

const usaStates = new UsaStates();

const styles = () => ({
  grid: {
    marginTop: 10,
    '&:first-child': {
      marginTop: 0,
    },
  },
  iconLink: {
    margin: '0 8px -7px 0',
  },
});

function onSubmit(values, dispatch, props) {
  const normalizeValues = (formValues) => {
    const normalizedValues = Object.assign({}, formValues, {});
    delete normalizedValues.taxonomies;
    delete normalizedValues['Referral Source'];
    return normalizedValues;
  };
  if (props.ID) {
    if (props.pristine) {
      return values;
    }
    const diffValues = normalizeValues(objectDiff(values, props.initialValues));
    const createdTaxonomies = values.taxonomies.filter(item =>
      props.initialValues.taxonomies.filter(initialItem =>
        initialItem.Description === item.Description).length === 0);
    const removedTaxonomies = props.initialValues.taxonomies.filter(initialItem =>
      values.taxonomies.filter(item =>
        item.Description === initialItem.Description).length === 0);
    const addedReferralSource = values['Referral Source'].filter(item =>
      props.initialValues['Referral Source'].filter(initialItem =>
        initialItem.value === item.value).length === 0);
    const removedReferralSource = props.initialValues['Referral Source'].filter(initialItem =>
      values['Referral Source'].filter(item =>
        item.value === initialItem.value).length === 0);
    return new Promise((resolve, reject) => {
      if (Object.keys(diffValues).length > 0) {
        dispatch(updatePrescriber({
          values: {
            ID: props.ID,
            ...diffValues,
            ...diffValues.DEA === ''
              && { DEA: null },
            ...diffValues.LIC === ''
              && { LIC: null },
            ...diffValues.Fax === ''
              && { Fax: null },
            ...diffValues.Email === ''
              && { Email: null },
            ...diffValues['Name Prefix'] === ''
              && { 'Name Prefix': null },
            ...diffValues['Name Suffix'] === ''
              && { 'Name Suffix': null },
            ...diffValues.Credential === ''
              && { Credential: null },
            ...diffValues.address ? {
              address: {
                ...diffValues.address,
                ...diffValues.address.Street === ''
                  && { Street: null },
                ...diffValues.address.Apt === ''
                  && { Apt: null },
                ...diffValues.address.City === ''
                  && { City: null },
                ...diffValues.address.State === ''
                  && { State: null },
                ...diffValues.address['Zip Code'] === ''
                  && { 'Zip Code': null },
              },
            } : {},
          },
          resolve,
          reject,
        }));
      } else {
        resolve();
      }
    })
      .then(() => new Promise((resolve, reject) => {
        if (createdTaxonomies.length > 0 || addedReferralSource.length > 0) {
          dispatch(addPrescriberResource({
            values: {
              ID: props.ID,
              ...createdTaxonomies.length > 0 ? { 'Taxonomies ID': createdTaxonomies } : {},
              ...addedReferralSource.length > 0 ? { 'Referral Source ID': addedReferralSource.map(item => item.value) } : {},
            },
            resolve,
            reject,
          }));
        } else {
          resolve();
        }
      }))
      .then(() => new Promise((resolve, reject) => {
        if (removedTaxonomies.length > 0 || removedReferralSource.length > 0) {
          dispatch(removePrescriberResource({
            values: {
              ID: props.ID,
              ...removedTaxonomies.length > 0 ? { 'Taxonomies ID': removedTaxonomies } : {},
              ...removedReferralSource.length > 0 ? { 'Referral Source ID': removedReferralSource.map(item => item.value) } : {},
            },
            resolve,
            reject,
          }));
        } else {
          resolve();
        }
      }))
      .then(res => res,
        (err) => {
          throw new SubmissionError({ _error: err.error.indexOf('Unique check fail') !== -1
            ? 'Sorry, but such Prescriber already exists' : err.error });
        });
  }
  return new Promise((resolve, reject) => {
    dispatch(createPrescriber({
      values: {
        ...normalizeValues(values),
        ...values.taxonomies ? { taxonomies: values.taxonomies } : {},
        ...values['Referral Source'] && values['Referral Source'].length > 0 ? { 'Referral Source ID': values['Referral Source'].map(item => item.value) } : {},
      },
      resolve,
      reject,
    }));
  }).then(res => res,
    (err) => {
      throw new SubmissionError({ _error: err.error });
    });
}

const PrescriberCreateEditForm = ({
  handleSubmit,
  change,
  array,
  className,
  error,
  actions,
  width,
  classes,
}) => (
  <form
    noValidate
    autoComplete="off"
    onSubmit={handleSubmit}
    className={className}
  >
    {error &&
      <Grid
        className={classes.grid}
        container
        spacing={24}
      >
        <Grid
          item
          xs={12}
        >
          <FormError>
            {error}
          </FormError>
        </Grid>
      </Grid>
    }
    <Grid
      className={classes.grid}
      container
      justify="center"
      spacing={24}
    >
      <Grid
        item
        xs={2}
        sm={2}
      >
        <Field
          id="Name Prefix"
          name="Name Prefix"
          label="Prefix"
          component={TextField}
          fullWidth
        />
      </Grid>
      <Grid
        item
        xs={10}
        sm={4}
      >
        <Field
          id="First Name"
          name="First Name"
          label="First Name"
          component={TextField}
          validate={[validate.required]}
          fullWidth
        />
      </Grid>
      <Grid
        item
        xs={10}
        sm={4}
      >
        <Field
          id="Last Name"
          name="Last Name"
          label="Last Name"
          component={TextField}
          validate={[validate.required]}
          fullWidth
        />
      </Grid>
      <Grid
        item
        xs={2}
        sm={2}
      >
        <Field
          id="Name Suffix"
          name="Name Suffix"
          label="Suffix"
          component={TextField}
          fullWidth
        />
      </Grid>
    </Grid>
    <Grid
      className={classes.grid}
      container
      justify="center"
      spacing={24}
    >
      <Grid
        item
        xs={6}
        sm={3}
      >
        <Field
          id="NPI"
          name="NPI"
          label="NPI"
          component={TextField}
          validate={[validate.required, validate.minLength10]}
          normalize={normalize.number}
          errorPlacement="bottom"
          maxLength="10"
          fullWidth
        />
      </Grid>
      <Grid
        item
        xs={6}
        sm={3}
      >
        <Field
          id="LIC"
          name="LIC"
          label="LIC"
          maxLength="12"
          component={TextField}
          errorPlacement="bottom"
          fullWidth
        />
      </Grid>
      <Grid
        item
        xs={6}
        sm={3}
      >
        <Field
          id="DEA"
          name="DEA"
          label="DEA"
          validate={[validate.minLength10]}
          maxLength="10"
          component={TextField}
          fullWidth
        />
      </Grid>
      <Grid
        item
        xs={6}
        sm={3}
      >
        <Field
          id="Credential"
          name="Credential"
          label="Credential"
          component={TextField}
          errorPlacement="bottom"
          fullWidth
        />
      </Grid>
    </Grid>
    <Grid
      className={classes.grid}
      container
      justify="center"
      spacing={24}
    >
      <Grid
        item
        xs={12}
        sm={6}
      >
        <Field
          id="Phone"
          name="Phone"
          label="Phone"
          component={TextField}
          validate={[validate.required, validate.phone]}
          format={value => (value ? value.replace('+1 ', '') : '')}
          normalize={normalize.phone}
          maxLength="14"
          errorPlacement="right"
          startAdornment={
            <InputAdornment position="start">
              <TextFieldAdornmentText>
                +1
              </TextFieldAdornmentText>
            </InputAdornment>
          }
          fullWidth
        />
      </Grid>
      <Grid
        item
        xs={12}
        sm={6}
      >
        <Field
          id="Cell Phone"
          name="Cell Phone"
          label="Cell Phone"
          component={TextField}
          validate={[validate.phone]}
          format={value => (value ? value.replace('+1 ', '') : '')}
          normalize={normalize.phone}
          maxLength="14"
          errorPlacement="left"
          startAdornment={
            <InputAdornment position="start">
              <TextFieldAdornmentText>
                +1
              </TextFieldAdornmentText>
            </InputAdornment>
          }
          fullWidth
        />
      </Grid>
    </Grid>
    <Grid
      className={classes.grid}
      container
      justify="center"
      spacing={24}
    >
      <Grid
        item
        xs={12}
        sm={5}
      >
        <Field
          id="Fax"
          name="Fax"
          label="Fax"
          component={TextField}
          validate={[validate.phone]}
          format={value => (value ? value.replace('+1 ', '') : '')}
          normalize={normalize.phone}
          maxLength="14"
          errorPlacement="left"
          startAdornment={
            <InputAdornment position="start">
              <TextFieldAdornmentText>
                +1
              </TextFieldAdornmentText>
            </InputAdornment>
          }
          fullWidth
        />
      </Grid>
      <Grid
        item
        xs={12}
        sm={7}
      >
        <Field
          id="Email"
          name="Email"
          label="Email"
          component={TextField}
          validate={[validate.email]}
          errorPlacement="top"
          fullWidth
        />
      </Grid>
    </Grid>
    <Grid
      className={classes.grid}
      container
      spacing={24}
    >
      <Grid item xs={9} sm={8}>
        <Field
          id="Street"
          name="address.Street"
          label="Street"
          component={AutosuggestField}
          validate={[validate.required]}
          placeholder="Lookup Address"
          fullWidth
          renderSuggestionContent={(suggestion) => suggestion.address.label}
          onSuggestionSelected={(suggestion) => {
            if (suggestion.address.city) {
              change('address.City', suggestion.address.city);
            }
            if (suggestion.address.postalCode) {
              change('address.Zip Code', suggestion.address.postalCode);
            }
            if (suggestion.address.stateCode) {
              change('address.State', suggestion.address.stateCode);
            }
            if (suggestion.address.street) {
              if (suggestion.address.houseNumber) {
                return `${suggestion.address.houseNumber} ${suggestion.address.street}`;
              }
              return suggestion.address.street;
            }
            return suggestion.address.label;
          }}
          fetchSuggestions={({ value }) => (
            value.length > 0
              ? requestFetchPlacesAutocomplete({
                q: value,
                in: 'countryCode:USA',
                limit: 5,
              }).then(res => res.items,
                err => err)
              : new Promise(resolve => resolve([]))
          )}
          errorPlacement="top"
        />
      </Grid>
      <Grid item xs={3} sm={4}>
        <Field
          id="Apt"
          name="address.Apt"
          label={width === 'xs' || width === 'sm' ? 'Suite' : 'Suite (Optional)'}
          component={TextField}
          fullWidth
        />
      </Grid>
    </Grid>
    <Grid
      className={classes.grid}
      container
      spacing={24}
    >
      <Grid item xs={6} sm={6}>
        <Field
          id="City"
          name="address.City"
          label="City"
          component={TextField}
          fullWidth
        />
      </Grid>
      <Grid item xs={3}>
        <Field
          id="State"
          name="address.State"
          label="State"
          component={AutosuggestField}
          normalize={normalize.uppercase}
          fullWidth
          renderSuggestionContent={suggestion => suggestion.abbreviation}
          onSuggestionSelected={suggestion => suggestion.abbreviation}
          fetchSuggestions={({ value, offset }) => (new Promise((resolve) => {
            if (offset === 0) {
              if (value.length > 0) {
                resolve(usaStates.states
                  .filter(item => item.abbreviation.indexOf(value) === 0));
              } else {
                resolve(usaStates.states);
              }
            } else {
              resolve([]);
            }
          }))}
        />
      </Grid>
      <Grid item xs={3}>
        <Field
          id="Zip Code"
          name="address.Zip Code"
          label="Zip Code"
          component={TextField}
          validate={[validate.zip]}
          normalize={normalize.zip}
          maxLength="5"
          fullWidth
        />
      </Grid>
    </Grid>
    <Grid
      className={classes.grid}
      container
      spacing={24}
    >
      <Grid item xs={12}>
        <Fields
          label={(
            <Fragment>
              <IconLink className={classes.iconLink} />
              Linked Referral Source
            </Fragment>
          )}
          names={[
            'Referral Source Name',
            'Referral Source',
          ]}
          autosuggestFieldName="Referral Source Name"
          fieldArrayName="Referral Source"
          placeholder="-Select Referral-"
          component={MultiselectAutosuggestField}
          renderSuggestionContent={suggestion =>
            suggestion['Name of source']}
          renderInputContent={suggestion =>
            suggestion['Name of source']}
          onSuggestionSelected={(suggestion) => {
            array.push('Referral Source', {
              value: suggestion.ID,
              label: suggestion['Name of source'],
            });
            return '';
          }}
          fetchSuggestions={({ value, limit, offset, fieldArrayValue }) => (
            value.length > 0 || (fieldArrayValue && fieldArrayValue.length > 0)
              ? requestSearchReferralSources({
                filters: [
                  ...value.length > 0
                    ? [{
                      name: 'Name of source',
                      operation: 'startswith',
                      val: value,
                      case_sensitivity: false,
                    }] : [],
                  ...fieldArrayValue && fieldArrayValue.length > 0
                    ? fieldArrayValue.map(arrayItem => ({
                      name: 'ID',
                      operation: 'ne',
                      val: arrayItem.value,
                    }))
                    : [],
                ],
                limit,
                offset,
              }).then(res => res.data,
                (err) => {
                  if (err.data.error === 'Need authorisation') {
                    actions.accountLoginFail();
                  }
                  return err;
                })
              : requestFetchReferralSources({
                limit,
                offset,
              }).then(res => res.data,
                (err) => {
                  if (err.data.error === 'Need authorisation') {
                    actions.accountLoginFail();
                  }
                  return err;
                })
          )}
          keepOpenAfterSelection
        />
      </Grid>
    </Grid>
    <Grid
      className={classes.grid}
      container
      justify="center"
      spacing={24}
    >
      <Grid
        item
        xs={12}
      >
        <Typography variant="caption">PECOS eligibility will be checked automatically</Typography>
      </Grid>
    </Grid>
  </form>
);

PrescriberCreateEditForm.propTypes = {
  handleSubmit: PropTypes.func.isRequired,
  change: PropTypes.func.isRequired,
  className: PropTypes.string,
  error: PropTypes.string,
  actions: PropTypes.shape({
    accountLoginFail: PropTypes.func.isRequired,
  }).isRequired,
  classes: PropTypes.shape({
    grid: PropTypes.string.isRequired,
  }).isRequired,
};

PrescriberCreateEditForm.defaultProps = {
  className: '',
  error: null,
};

const mapDispatchToProps = dispatch => ({
  actions: bindActionCreators({ accountLoginFail }, dispatch),
});

export default connect(null, mapDispatchToProps)(reduxForm({
  onSubmit,
})(withStyles(styles)(withWidth()(PrescriberCreateEditForm))));



// WEBPACK FOOTER //
// ./src/components/settings/PrescriberCreateEditDialog/PrescriberCreateEditForm.js